import React, { useCallback, useState } from 'react';
import { cn } from '../../utils/cn';
import { Asset, getAssetIconByExtension } from '../../../core/types/asset.types';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { useAssetUrl, isImageAssetType, isVideoAssetType } from '../../hooks/useAssetUrl';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';

interface AssetItemProps {
  asset: Asset;
  isSelected: boolean;
  onSelect: (id: string, multiSelect?: boolean) => void;
  size: 'small' | 'medium' | 'large';
}

export const AssetItem: React.FC<AssetItemProps> = ({ asset, isSelected, onSelect, size }) => {
  const { setDraggedAsset, showContextMenu } = useProjectPanelStore();
  const [isDragging, setIsDragging] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Load thumbnail for image and video assets
  const isImage = isImageAssetType(asset.type);
  const isVideo = isVideoAssetType(asset.type);
  const shouldLoadThumbnail = (isImage || isVideo) && !imageError;
  const { url: thumbnailUrl, loading: thumbnailLoading } = useAssetUrl(
    shouldLoadThumbnail ? asset.path : null
  );

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    onSelect(asset.id, e.ctrlKey || e.metaKey);
  }, [asset.id, onSelect]);

  const handleDoubleClick = useCallback(() => {
    const { openPreview } = useAssetPreviewStore.getState();

    switch (asset.type) {
      case 'sprite':
      case 'video':
      case 'audio-music':
      case 'audio-sfx':
      case 'prefab':
        // Open floating preview panel
        openPreview(asset);
        break;

      case 'scene':
        // Load scene (auto-caches current scene)
        import('../../store/sceneStore').then(({ useSceneStore }) => {
          useSceneStore.getState().loadScene(asset.path).catch((error) => {
            console.error('Failed to load scene:', error);
          });
        });
        break;

      default:
        // Show NoPreview panel for unknown types
        openPreview(asset);
    }
  }, [asset]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    onSelect(asset.id);
    showContextMenu(e.clientX, e.clientY, asset.id);
  }, [asset.id, onSelect, showContextMenu]);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', asset.id);
    setDraggedAsset(asset.id);
    setIsDragging(true);
  }, [asset.id, setDraggedAsset]);

  const handleDragEnd = useCallback(() => {
    setDraggedAsset(null);
    setIsDragging(false);
  }, [setDraggedAsset]);

  const sizeClass = { small: 'w-20 h-20', medium: 'w-24 h-24', large: 'w-32 h-32' }[size];
  const iconSize = { small: 'text-2xl', medium: 'text-3xl', large: 'text-4xl' }[size];
  const icon = getAssetIconByExtension(asset);

  const bgColor: Record<string, string> = {
    sprite: 'bg-gradient-to-br from-zinc-700 to-zinc-800',
    video: 'bg-gradient-to-br from-indigo-900/30 to-zinc-800',
    'audio-music': 'bg-gradient-to-br from-purple-900/30 to-zinc-800',
    'audio-sfx': 'bg-gradient-to-br from-pink-900/30 to-zinc-800',
    'script-ts': 'bg-gradient-to-br from-blue-900/30 to-zinc-800',
    'script-js': 'bg-gradient-to-br from-yellow-900/30 to-zinc-800',
    prefab: 'bg-gradient-to-br from-green-900/30 to-zinc-800',
    scene: 'bg-gradient-to-br from-orange-900/30 to-zinc-800',
  };

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(
        'flex flex-col items-center p-2 rounded cursor-pointer transition-all',
        'hover:bg-zinc-700/50',
        isSelected && 'bg-blue-600/50 ring-1 ring-blue-500',
        isDragging && 'opacity-50'
      )}
    >
      <div className={cn(
        'flex items-center justify-center rounded mb-1',
        sizeClass,
        bgColor[asset.type] || 'bg-zinc-700',
        asset.type === 'sprite' && 'bg-checkerboard'
      )}>
        {shouldLoadThumbnail && thumbnailLoading ? (
          <div className="w-3/4 h-3/4 rounded bg-zinc-600 animate-pulse" />
        ) : thumbnailUrl && isVideo ? (
          <video
            src={thumbnailUrl + '#t=0.1'}
            className="max-w-full max-h-full object-contain"
            preload="metadata"
            muted
            playsInline
            onError={handleImageError}
          />
        ) : thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={asset.name}
            className="max-w-full max-h-full object-contain"
            onError={handleImageError}
            loading="lazy"
          />
        ) : (
          <span className={iconSize}>{icon}</span>
        )}
      </div>
      <div className={cn('w-full text-center text-xs truncate px-1', isSelected ? 'text-white' : 'text-zinc-300')}>
        {asset.name}
      </div>
    </div>
  );
};
